import { useEffect } from 'react';

export const DISMISSIBLE_SELECTION_CONTROL_ATTRIBUTE = 'data-selection-control';

export const DISMISSIBLE_SELECTION_CONTROL_SELECTOR = [
    `[${DISMISSIBLE_SELECTION_CONTROL_ATTRIBUTE}="true"]`,
    '[data-compact-list-selection-control="true"]',
    '.ant-dropdown',
    '.ant-modal',
    '.ant-popover',
].join(', ');

interface UseDismissibleSelectionOptions {
    active: boolean;
    onDismiss: () => void;
}

/**
 * Applies the platform selection-dismiss contract: Escape always clears the
 * active selection, while pointer actions outside selection controls clear it
 * before the next interaction begins.
 */
export const useDismissibleSelection = ({ active, onDismiss }: UseDismissibleSelectionOptions) => {
    useEffect(() => {
        if (!active) {
            return;
        }

        const dismiss = () => {
            if (
                document.activeElement instanceof HTMLElement &&
                document.activeElement.closest(DISMISSIBLE_SELECTION_CONTROL_SELECTOR)
            ) {
                document.activeElement.blur();
            }

            onDismiss();
        };
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                dismiss();
            }
        };
        const handleOutsidePointerDown = (event: PointerEvent) => {
            if (event.target instanceof Element && event.target.closest(DISMISSIBLE_SELECTION_CONTROL_SELECTOR)) {
                return;
            }

            dismiss();
        };
        const handleFocusOutside = (event: FocusEvent) => {
            if (event.target instanceof Element && event.target.closest(DISMISSIBLE_SELECTION_CONTROL_SELECTOR)) {
                return;
            }

            dismiss();
        };

        window.addEventListener('keydown', handleEscape, true);
        window.addEventListener('pointerdown', handleOutsidePointerDown, true);
        window.addEventListener('focusin', handleFocusOutside, true);

        return () => {
            window.removeEventListener('keydown', handleEscape, true);
            window.removeEventListener('pointerdown', handleOutsidePointerDown, true);
            window.removeEventListener('focusin', handleFocusOutside, true);
        };
    }, [active, onDismiss]);
};
